/**
 * Owner-selectable video models for storyboard generation.
 *
 * The content-generation service is a thin proxy over the underlying providers (OpenAI, BytePlus,
 * Google/Vertex), so a model `id` is passed through to it verbatim. This list is the curated set of
 * ids confirmed video-capable for the environment; adding one (e.g. BytePlus Seedance, once its exact
 * id is enabled upstream) is a one-line entry here plus the matching backend allow-list. `label` is a
 * product name (a proper noun), shown as-is in the dropdown.
 */
export interface VideoModelOption {
  id: string;
  label: string;
  /** The clip durations (seconds) this model accepts. Any other value is rejected upstream — Veo 500s
   *  on odd durations, accepting only 4/6/8 — so the duration dropdown and generation are limited to these. */
  durations: number[];
  /** Rough wall-clock MINUTES a single beat takes on this model (frame edit + the video draw), for the
   *  "about N min to generate" estimate. Veo is fast (~1min/beat); BytePlus Seedance is a slow
   *  submit-and-poll model (a bare 4s clip already ~2min, keyframe beats slower), so it is much higher. */
  etaMinPerBeat: number;
  /** The model DERIVES its output aspect from the (start) frame it is given rather than an `aspect_ratio`
   *  param (the backend's `ratioFromFirstFrame`). Since the storyboard always supplies a start frame sized to
   *  the requested ratio, these models can produce ANY ratio we render. Models WITHOUT this (Veo, Omni) take
   *  the aspect string verbatim and the provider only accepts 16:9 / 9:16. Drives which ratios are offered. */
  derivesAspectFromFrame: boolean;
  /** The underlying provider (a proper noun), shown in the model info tooltip. */
  provider: string;
  /** One-line "what it produces" note for the info tooltip (English proper-noun-heavy copy, rendered raw). */
  blurb: string;
  /** Hidden from the FE model picker, but kept fully wired: the registry entry stays so saved drafts that
   *  reference it, `resolveVideoModel`, `durationsForModel`, the estimate and the label lookup all still
   *  work. Re-show the model in the UI later by simply dropping this flag (no other change needed). */
  hidden?: boolean;
  /** Single clip (v2): the ONE-clip lengths (seconds) this model can render (spec section 3; mirrored by the
   *  backend `clipLengths`). Distinct from the v1 per-beat `durations`. */
  clipLengths: number[];
  /** Single clip (v2): the default clip length for a new ad on this model. */
  defaultClipLength: number;
  /** Single clip (v2): rough wall-clock MINUTES a whole single-clip run takes on this model (opening frame +
   *  the clip + overlay + upload), for the "about N min" estimate and the time-based progress bar. */
  etaMinPerClip: number;
  /** Single clip (v2): the model picks the real length itself (Omni steers duration in the prompt, 3-10s), so
   *  its length is shown as approximate ("≈10s"). */
  approxClipLength?: boolean;
}

// The storyboard interpolates each beat between an image start frame and a last_frame, so only a
// keyframe-capable model works. Veo 3.1 Fast is verified end-to-end and is the default. Omni 1.1 Flash is
// documented as keyframe-capable (image + last_frame) and cheaper (esp. at 360p). BytePlus Seedance 2.5
// supports start/end frames (480p/720p, 4-30s, fixed 24fps) and is a cheaper alternative to Veo. All are
// kept in sync with the backend registry in pkg/jobs/videomodels.go.
// The single-clip (v2) columns (`clipLengths` / `defaultClipLength` / `etaMinPerClip`) mirror the spec's per-model
// table verbatim (.claude/specs/2026-09-28-video-single-clip-v2.md section 3) and the backend `videoModelSpec.clipLengths`.
export const VIDEO_MODELS: VideoModelOption[] = [
  { id: 'veo-3.1-fast-generate-001', label: 'Veo 3.1 Fast', durations: [4, 6, 8], etaMinPerBeat: 1, derivesAspectFromFrame: false, provider: 'Google', blurb: 'Cinematic, coherent and the fastest here.', clipLengths: [4, 6, 8], defaultClipLength: 8, etaMinPerClip: 2 },
  { id: 'gemini-omni-1.1-flash-preview', label: 'Omni 1.1 Flash', durations: [4, 6, 8], etaMinPerBeat: 1, derivesAspectFromFrame: false, provider: 'Google', blurb: "Google's budget model: lighter detail, fast and cheap.", clipLengths: [10], defaultClipLength: 10, etaMinPerClip: 2, approxClipLength: true },
  { id: 'dreamina-seedance-2-5', label: 'Seedance 2.5', durations: [4, 6, 8], etaMinPerBeat: 4.5, derivesAspectFromFrame: true, provider: 'BytePlus', blurb: 'Rich, dynamic motion. Slower (submit-and-poll) and supports any aspect ratio.', clipLengths: [5, 10, 15, 20, 30], defaultClipLength: 15, etaMinPerClip: 8 },
  { id: 'dreamina-seedance-2-0', label: 'Seedance 2.0', durations: [4, 6, 8], etaMinPerBeat: 4.5, derivesAspectFromFrame: true, provider: 'BytePlus', blurb: 'Previous-gen Seedance: cheaper. Slower, any aspect ratio.', clipLengths: [5, 10, 15], defaultClipLength: 15, etaMinPerClip: 5 },
  // MiniMax is hidden from the picker for now (kept wired; drop `hidden` to re-show).
  { id: 'minimax-h3', label: 'MiniMax H3', durations: [5, 6, 8], etaMinPerBeat: 3, derivesAspectFromFrame: true, provider: 'MiniMax', blurb: 'Mid-speed and solid quality. Any aspect ratio.', hidden: true, clipLengths: [6, 10, 15], defaultClipLength: 10, etaMinPerClip: 4 },
  { id: 'minimax-h3-max', label: 'MiniMax H3 Max', durations: [5, 6, 8], etaMinPerBeat: 3, derivesAspectFromFrame: true, provider: 'MiniMax', blurb: "MiniMax's higher-quality tier: mid-speed. Any aspect ratio.", hidden: true, clipLengths: [6, 10, 15], defaultClipLength: 10, etaMinPerClip: 4 },
];

/** The models offered in the FE picker: the registry minus any `hidden` entry. Wiring (resolve, durations,
 *  estimate, label) still uses the full {@link VIDEO_MODELS}, so a hidden model stays fully functional. */
export const selectableVideoModels = (): VideoModelOption[] => VIDEO_MODELS.filter((m) => !m.hidden);

/** The default video model (first in the list) used until the owner picks another. */
export const DEFAULT_VIDEO_MODEL = VIDEO_MODELS[0].id; // veo-3.1-fast-generate-001

/** The durations a model accepts (falls back to the default model's set for an unknown id). */
export const durationsForModel = (modelId: string): number[] =>
  (VIDEO_MODELS.find((m) => m.id === modelId) ?? VIDEO_MODELS[0]).durations;

/** Snap a duration to the nearest value the given list supports (ties → the shorter). */
export const snapToDurations = (seconds: number, durations: number[]): number =>
  durations.reduce((best, v) => (Math.abs(v - seconds) < Math.abs(best - seconds) ? v : best), durations[0]);

/** Rough "about N min to generate" estimate for a beat count on a given model: beats × the model's
 *  per-beat minutes, plus ~1 min of fixed overhead (key-visual pad + assembly + upload). Veo is fast
 *  (~beats+1); Seedance is much slower. Falls back to the default model's rate for an unknown id. */
export const estimatedMinutes = (beats: number, modelId: string): number =>
  Math.max(1, Math.round(estimatedRunMs(beats, modelId) / 60_000));

/** ~1 min of fixed run overhead (key-visual pad + assembly + upload), folded into the raw-ms estimate below
 *  (and thus into {@link estimatedMinutes}, which is derived from it). */
const RUN_OVERHEAD_MS = 60_000;

/** Raw wall-clock estimate in MILLISECONDS for a full run (beats × the model's per-beat time + overhead), the
 *  un-rounded basis for driving the progress bar's continuous, time-based advance. Falls back to the default
 *  model's rate for an unknown id. */
export const estimatedRunMs = (beats: number, modelId: string): number => {
  const model = VIDEO_MODELS.find((m) => m.id === modelId) ?? VIDEO_MODELS[0];
  return Math.max(RUN_OVERHEAD_MS, beats * model.etaMinPerBeat * 60_000 + RUN_OVERHEAD_MS);
};

/* ---------- single clip (v2) ---------- */

const modelOf = (modelId: string): VideoModelOption => VIDEO_MODELS.find((m) => m.id === modelId) ?? VIDEO_MODELS[0];

/** The single-clip lengths a model can render (falls back to the default model's set for an unknown id). */
export const clipLengthsForModel = (modelId: string): number[] => modelOf(modelId).clipLengths;

/** The default single-clip length for a new ad on a model. */
export const defaultClipLengthFor = (modelId: string): number => modelOf(modelId).defaultClipLength;

/** Whether the model only approximates the requested length (Omni: shown as "≈10s"). */
export const isApproxClipLength = (modelId: string): boolean => modelOf(modelId).approxClipLength === true;

/** Clamp a clip length to one the model renders: the LARGEST legal length <= L, else the model's smallest (an
 *  undefined / non-positive L resolves to the model's default). Used on a model / Format switch. */
export const clampClipLength = (L: number | undefined, modelId: string): number => {
  if (!L || L <= 0) return defaultClipLengthFor(modelId);
  const lengths = [...clipLengthsForModel(modelId)].sort((a, b) => a - b);
  const fitting = lengths.filter((v) => v <= L);
  return fitting.length > 0 ? fitting[fitting.length - 1] : lengths[0];
};

/** The credit cost of a single-clip ad, identical to the backend (spec section 3): ceil(L / 8) beat units times the
 *  model's per-beat price. `perBeat` is the caller's already-resolved `videoBeatCostFor(pricing, model)` (kept
 *  as an argument so this module stays free of store imports); undefined until priced. */
export const singleClipCost = (perBeat: number | undefined, L: number | undefined): number | undefined =>
  perBeat === undefined || !L || L <= 0 ? undefined : Math.ceil(L / 8) * perBeat;

/** A single-clip run reports 4 progress units (spec 4.6): opening frame, clip, overlay, upload. */
export const SINGLE_CLIP_UNITS = 4;

/** Share of a single-clip run's wall-clock each progress unit takes (opening frame, clip, overlay, upload). The
 *  clip dominates, so an even split would crawl through the opening frame and then stall inside the clip. */
const SINGLE_CLIP_UNIT_WEIGHTS = [0.15, 0.7, 0.1, 0.05];

/** "About N min" for a single-clip run on a model (its `etaMinPerClip`, at least 1). */
export const estimatedClipMinutes = (modelId: string): number => Math.max(1, Math.round(modelOf(modelId).etaMinPerClip));

/** Estimated MILLISECONDS of one single-clip progress unit (0-based, as `completed` counts them), for the
 *  time-based progress bar. Out-of-range units read as the smallest weight. */
export const singleClipUnitMs = (unit: number, modelId: string): number =>
  modelOf(modelId).etaMinPerClip * 60_000 * (SINGLE_CLIP_UNIT_WEIGHTS[unit] ?? SINGLE_CLIP_UNIT_WEIGHTS[3]);

/** Estimated MILLISECONDS of the run unit a video ad is on (`completed` units done), for the time-based progress
 *  bar: a stitched-beats (v1) run spreads its whole estimate evenly over its units (frames and clips); a single clip
 *  (v2) weights each of its 4 units (the clip dominates). */
export const runUnitMs = (r: {
  pipeline: 'beats' | 'single_clip';
  beats: number;
  total: number;
  completed: number;
  model: string;
}): number =>
  r.pipeline === 'single_clip'
    ? singleClipUnitMs(r.completed, r.model)
    : estimatedRunMs(r.beats, r.model) / Math.max(1, r.total);

/** A running video ad's progress %: the completed share, plus the current unit filled over its estimated time
 *  (capped at 90% of the unit, so it never claims the next milestone early), held below 100 until the run ends.
 *  Shared by the editor's bar and the library card's. */
export const runProgressPct = (r: { completed: number; total: number; inUnitMs: number; estUnitMs: number }): number => {
  if (r.total <= 0) return 0;
  const realPct = (100 * r.completed) / r.total;
  const nextPct = (100 * Math.min(r.completed + 1, r.total)) / r.total;
  const frac = r.estUnitMs > 0 ? Math.min(0.9, Math.max(0, r.inUnitMs) / r.estUnitMs) : 0;
  return Math.min(97, realPct + frac * (nextPct - realPct));
};

/** The Target length a stitched-beats (v1) video ad uses when none is saved: new ads start here, and a reopened ad
 *  without a saved length reads it too. Single-clip (v2) ads use their model's own default instead. */
export const DEFAULT_V1_TARGET_SECONDS = 10;
